import React, { useState } from "react";
import { useQuery } from "react-query";
import { Loader } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { getPcapDownloadData } from "@services/api/amlAnomalyDetection";
import { Button } from "@components/ui/button";
import { Label } from "@components/ui/label";
import { toast } from "@components/ui/use-toast";
import { EventProp } from "@components/common/CommonTypes";

interface AMLDropdownmenuProps {
  events: EventProp[];
  sensor: string;
  show: boolean;
  onClose: () => void;
}

const PcapDownloadDialog: React.FC<AMLDropdownmenuProps> = ({
  events = [],
  sensor,
  show,
  onClose,
}) => {
  const [pcapData, setPcapData] = useState<EventProp>(events[0]);

  const [pcapLoader, setpcapLoader] = useState(false);

  const { data: pcapDownloadData, refetch: refetchPcapData } = useQuery(
    ["getPcapDownloadData"],
    () =>
      getPcapDownloadData({
        eventId: pcapData.EventId,
        gid: pcapData.Gid,
        time: pcapData.Time,
        sensor,
        eventname: pcapData.Name,
      }),
    { enabled: false },
  );

  React.useEffect(() => {
    if (pcapLoader) {
      if (pcapDownloadData) {
        if (pcapDownloadData.headers["content-length"] > 0) {
          const url = window.URL.createObjectURL(
            new Blob([pcapDownloadData.data]),
          );
          const link = document.createElement("a");
          link.setAttribute("href", url);
          link.setAttribute(
            "download",
            `${pcapData.Name || pcapData.EventId}.pcap`,
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
        } else {
          toast({
            title: "No Data",
            description:
              "Pcap is still being generated. Please try again later",
            variant: "destructive",
          });
        }
        setpcapLoader(false);
      } else {
        toast({
          title: "Error",
          description: "An error occured while loading pcap data",
          variant: "destructive",
        });
      }
    }
  }, [pcapDownloadData]);

  function downloadPcap() {
    setpcapLoader(true);
    refetchPcapData();
  }

  return (
    <>
      <Dialog open={show} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[25vw]">
          <DialogHeader>
            <DialogTitle>Download Pcap</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="flex items-center gap-4">
              <Label htmlFor="fixedAnomalyReason">Pcap</Label>
              <Select
                value={pcapData.EventId as string}
                onValueChange={(eid) =>
                  setPcapData(events.find((e) => e.EventId == eid) || events[0])
                }
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {events.map((ev) => (
                      <SelectItem key={ev.EventId} value={ev.EventId as string}>
                        {ev.Name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            {pcapLoader ? (
              <Button type="submit">
                <Loader className="animate-spin" />
              </Button>
            ) : (
              <Button type="submit" onClick={downloadPcap}>
                Download
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PcapDownloadDialog;
